// LunaInsight — renders the Luna recommendation string as a bold verdict line
// plus a real bullet list, instead of dumping the raw markdown-lite text
// (which showed literal `*APPROVE*` and `•` glyphs).
//
// The backend `aiInsight` is a plain string shaped like:
//   I recommend *APPROVE* for the following reasons:
//   • Previous decisions for this resource were *approved*
//   • All employees in the Leader department have access
//
// We parse two things: inline emphasis (`*x*` / `**x**` → bold) and bullet
// lines (prefixed with •, -, or "* "). Everything inherits the LunaCard body
// type (12px / neutral.900 / line-height 1.5) — no font-resetting wrappers.

import { Box } from "@chakra-ui/react";
import { Fragment, ReactNode } from "react";

// `*emphasis*` or `**emphasis**` → bold. A lone "* " (bullet) never matches
// because the regex requires a non-`*` run terminated by another `*`.
const EMPHASIS = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;

const renderInline = (text: string): ReactNode[] =>
  text
    .split(EMPHASIS)
    .filter((seg) => seg.length > 0)
    .map((seg, i) => {
      const m = seg.match(/^\*\*?([^*]+)\*\*?$/);
      if (m) {
        return (
          <Box as="strong" key={i} fontWeight={600} color="neutral.950">
            {m[1]}
          </Box>
        );
      }
      return <Fragment key={i}>{seg}</Fragment>;
    });

const BULLET = /^\s*(?:[•*-])\s+(.*)$/;

type Block =
  | { kind: "p"; text: string }
  | { kind: "ul"; items: string[] };

// Group consecutive bullet lines into a single list; everything else is a
// paragraph. Blank lines are dropped.
const toBlocks = (raw: string): Block[] => {
  const blocks: Block[] = [];
  for (const line of raw.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const bullet = trimmed.match(BULLET);
    if (bullet) {
      const last = blocks[blocks.length - 1];
      if (last && last.kind === "ul") last.items.push(bullet[1]);
      else blocks.push({ kind: "ul", items: [bullet[1]] });
    } else {
      blocks.push({ kind: "p", text: trimmed });
    }
  }
  return blocks;
};

export interface LunaInsightProps {
  text: string;
}

const LunaInsight = ({ text }: LunaInsightProps) => {
  const blocks = toBlocks(text);
  return (
    <>
      {blocks.map((block, i) =>
        block.kind === "ul" ? (
          <Box as="ul" key={i}>
            {block.items.map((item, j) => (
              <Box as="li" key={j}>
                {renderInline(item)}
              </Box>
            ))}
          </Box>
        ) : (
          <Box key={i} mb={i < blocks.length - 1 ? "2xs" : 0}>
            {renderInline(block.text)}
          </Box>
        ),
      )}
    </>
  );
};

export default LunaInsight;
